import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  House, Users, ListBullets, CheckSquare, UploadSimple,
  ChartBar, Gear, SignOut, List, X, MapTrifold, Storefront,
  CalendarBlank,
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { initials } from "../../lib/utils";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: House },
  { to: "/leads", label: "Leads", Icon: ListBullets },
  { to: "/activities", label: "Activities", Icon: CheckSquare },
  { to: "/calendar", label: "Calendar", Icon: CalendarBlank },
  { to: "/catalog", label: "Catalog", Icon: Storefront },
  { to: "/map", label: "Lead Map", Icon: MapTrifold },
  { to: "/import", label: "Import CSV", Icon: UploadSimple },
  { to: "/reports", label: "Agents Report", Icon: ChartBar },
  { to: "/agents", label: "Agents", Icon: Users },
  { to: "/settings", label: "Settings", Icon: Gear },
];

const AGENT_NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: House },
  { to: "/leads", label: "My Leads", Icon: ListBullets },
  { to: "/activities", label: "My Activities", Icon: CheckSquare },
  { to: "/calendar", label: "Calendar", Icon: CalendarBlank },
  { to: "/catalog", label: "Catalog", Icon: Storefront },
  { to: "/settings", label: "Settings", Icon: Gear },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = user?.role === "admin" ? ADMIN_NAV : AGENT_NAV;

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  // Auto-close drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const Sidebar = ({ mobile = false }) => (
    <aside
      data-testid="app-sidebar"
      className={`${mobile ? "" : "hidden lg:flex fixed inset-y-0 left-0 z-40"} w-64 bg-[#1A3A21] text-gray-200 flex-col flex h-[100dvh]`}
    >
      <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center p-1 shadow-sm" data-testid="sidebar-logo">
            <img
              src="/assets/gardaj-logo.png"
              alt="GardaJ Distribution"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <div className="font-display text-white text-lg tracking-tight leading-none">GARDAJ</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">Distribution CRM</div>
          </div>
        </div>
        {mobile && (
          <button onClick={() => setMobileOpen(false)} className="text-white/70 hover:text-white">
            <X size={22} />
          </button>
        )}
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {items.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
          >
            <Icon size={18} weight="duotone" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[#E1A104] text-[#1A3A21] flex items-center justify-center font-bold text-sm">
            {initials(user?.name || user?.email)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">
              {user?.name || "User"}
            </div>
            <div className="text-[11px] text-white/60 uppercase tracking-wider">{user?.role}</div>
          </div>
          <button
            onClick={doLogout}
            title="Logout"
            data-testid="logout-button"
            className="p-2 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#111827]">
      <Sidebar />

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0">
            <Sidebar mobile />
          </div>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 rounded-md hover:bg-gray-100"
            data-testid="mobile-menu-button">
            <List size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-white flex items-center justify-center p-0.5 border border-gray-200" data-testid="mobile-topbar-logo">
              <img src="/assets/gardaj-logo.png" alt="GardaJ" className="w-full h-full object-contain" />
            </div>
            <span className="font-display font-bold text-sm">GARDAJ</span>
          </div>
        </div>
        <div className="px-4 md:px-8 lg:px-10 py-6 lg:py-8 max-w-[1600px] w-full">{children}</div>
      </main>
    </div>
  );
}
